import * as React from "react";
import {InputGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {LcdIcon} from "../icon/LcdIcon";
import {Spinner} from "../spinner/Spinner";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";

const MESSAGES = defineMessages({
  NONE: {
    id: "studio.property.edit.none",
    defaultMessage: "None"
  }
});

interface TextEditProps {
  name?: string;
  value: string;
  defaultValue?: string;
  type?: string;
  onChange: (newValue: string) => void | Promise<void>;
  onBlur?: React.FocusEventHandler<any>;
  readOnly?: boolean;
}

interface TextEditState {
  isMouseOver: boolean;
  isEditing: boolean;
  editingValue: string;
  submitState: "NOT_STARTED" | "SUBMITTING" | "ERROR"; //no explicit FINISHED state, the edit field just goes back to 'display' mode
  submitError: Error;
  lastMouseOut: number;
}

/**
 * A text edit field that can be edited upon mouseover, just like a JIRA text field.
 */
class TextEditComponent extends React.Component<TextEditProps & InjectedIntlProps, TextEditState> {

  _textInput: HTMLInputElement;

  constructor(props) {
    super(props);
    this.state = {
      isMouseOver: false,
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
      lastMouseOut: 0,
    };
  }

  reset = () => {
    this.setState({
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
    });
  }

  handleMouseOver = () => {
    // Remove this time hack when implementing a real solution for LF-2229
    if (Date.now() - this.state.lastMouseOut > 20) {
      this.setState({isMouseOver: true});
    }
  }

  handleMouseOut = () => {
    this.setState({isMouseOver: false, lastMouseOut: Date.now()});
  }

  handleChange = () => {
    const newValue = this._textInput.value;
    //remove the error state when changing the input after an error
    this.setState(prevState => ({
      editingValue: newValue,
      submitState: prevState.submitState === "ERROR" ? "NOT_STARTED" : prevState.submitState
    }));
  }

  handleInputFocus = () => {
    if (this._textInput) {
      this._textInput.select();
    }
    this.setState(prevState => ({
      isEditing: true,
      editingValue: prevState.submitState === "ERROR" ? prevState.editingValue : this.props.value
    }));
  }

  handleInputBlur = (blurEvent) => {
    if (this.state.editingValue !== this.props.value && (this.state.submitState !== "ERROR")) {
      this.valueChanged(this.state.editingValue).then(this.reset).catch(this.reset);
    } else {
      this.reset();
    }
    if (this.props.onBlur) {
      this.props.onBlur(blurEvent);
    }
  }

  valueChanged = (newValue: string): Promise<void> => {
    const onChangeResult = this.props.onChange(newValue);
    if (onChangeResult instanceof Promise) {
      this.setState({submitState: "SUBMITTING"});
      const onChangeResultPromise = onChangeResult as Promise<void>;
      return onChangeResultPromise.catch((error) => {
        this.setState({submitState: "ERROR", submitError: error});
        this._textInput.focus();
        throw error;
      });
    } else {
      return Promise.resolve();
    }
  }

  handleKeyPress = (event: React.KeyboardEvent<any>) => {
    //submit on ENTER
    if (event.key === 'Enter' && this._textInput) {
      this._textInput.blur();
    }
  }

  shouldShowInput = () => {
    const {isMouseOver, isEditing} = this.state;
    return isMouseOver || isEditing;
  }

  escapeKeyPressedListener = (event: KeyboardEvent) => {
    //cancel on ESC, unfortunately ESC keys are only dispatched on document
    if (event.key === 'Escape' && this._textInput) {
      //revert editingValue to this.props.value -> no onChange fired
      this.setState({editingValue: this.props.value});
      this._textInput.blur();
    }
  }

  renderInput = () => {
    if (this.shouldShowInput() && !this.props.readOnly) {
      const inputValue = this.state.isEditing ? this.state.editingValue : this.props.value;
      document.addEventListener("keyup", this.escapeKeyPressedListener);
      //use a 'vanilla' input iso a FormControl to allow more low-level handling of events (select/focus/blur methods)
      return <input type={this.props.type || "text"}
                    className="form-control textEditInput"
                    ref={(ref) => {
                      this._textInput = ref;
                    }}
                    onBlur={this.handleInputBlur}
                    onFocus={this.handleInputFocus}
                    onChange={this.handleChange}
                    onKeyPress={this.handleKeyPress}
                    name={this.props.name}
                    value={inputValue || ""}
      />;
    }
    this._textInput = null;
    document.removeEventListener("keyup", this.escapeKeyPressedListener);
    return (<div className="textEditDisplay">
      {this.props.children || this.props.value || this.props.defaultValue ||
       this.props.intl.formatMessage(MESSAGES.NONE)}
    </div>);
  }

  renderAddon = () => {
    if (this.props.readOnly) {
      return null;
    }
    const {submitState, submitError} = this.state;
    switch (submitState) {
    case "NOT_STARTED":
      return null;
    case "SUBMITTING":
      return <InputGroup.Addon><Spinner/></InputGroup.Addon>;
    case "ERROR": {
      const errorMessage = submitError.message;
      const tooltip = <Tooltip id={(this.props.name || "textedit") + "-error-tooltip"}>{errorMessage}</Tooltip>;
      return (
          <InputGroup.Addon>
            <OverlayTrigger placement="top" overlay={tooltip} defaultOverlayShown={true}>
              <LcdIcon icon="alert"/>
            </OverlayTrigger>
          </InputGroup.Addon>
      );
    }
    }
  }

  render() {

    return (
        <InputGroup className="textEditContainer"
                    onMouseOver={this.handleMouseOver}
                    onMouseOut={this.handleMouseOut}>
          {this.renderInput()}{this.renderAddon()}
        </InputGroup>
    );

  }

}

export const TextEdit = injectIntl(TextEditComponent);